/**
 * Centralized Site Configuration for S.K. Woollens
 * Easy content management for contact numbers, partners, social links, specs, and international sourcing markets.
 */

export const siteConfig = {
  company: {
    name: "S.K. Woollens",
    legalName: "S.K. Woollens",
    legacyName: "Bulaki Chand Kesari Chand",
    legacyStatement: "S.K. Woollens carries a wool-industry legacy dating back to 1965. Our current business operates under the name S.K. Woollens, while our Google Maps business listing is currently associated with the legacy name Bulaki Chand Kesari Chand.",
    tagline: "Trusted Wool Supply Since 1965",
    supportingStatement: "Raw Wool Sourcing & Supply for Yarn Manufacturers",
    fullStatement:
      "S.K. Woollens is a Bikaner-based wool trading and supply company with a legacy dating back to 1965, supplying raw wool to yarn manufacturers through reliable sourcing, industry relationships and consistent B2B supply.",
    establishedYear: 1965,
    headquarters: "Bikaner, Rajasthan, India",
    addressLine: "B-26, Un Mandi, Antyodaya Nagar, Bikaner, Rajasthan 334001, India",
    businessType: "B2B Raw Wool Trading & Sourcing",
    primaryMarket: "Yarn Manufacturers & Carpet Yarn Spinners",
  },

  partners: [
    {
      id: "nikhil-banthia",
      name: "Nikhil Banthia",
      role: "Partner — S.K. Woollens",
      phone: "7014581250",
      phoneRaw: "7014581250",
      whatsapp: "https://wa.me/917014581250?text=Hi%2C%20I%20would%20like%20to%20connect%20with%20you%20regarding%20wool%20sourcing%20and%20supply",
      email: "NIKHILBANTH783@GMAIL.COM",
      image: null,
      focus: "International Sourcing, Sourcing Strategy & B2B Client Partnerships",
    },
    {
      id: "sunil-banthia",
      name: "Sunil Banthia",
      role: "Partner — S.K. Woollens",
      phone: "9829109294",
      phoneRaw: "9829109294",
      whatsapp: "https://wa.me/919829109294",
      email: "NIKHILBANTH783@GMAIL.COM",
      image: null,
      focus: "Domestic Wool Procurement, Bikaner Market Operations & Quality Control",
    },
  ],

  contacts: {
    primaryPhone: "7014581250",
    secondaryPhone: "9829109294",
    primaryEmail: "NIKHILBANTH783@GMAIL.COM",
    whatsappNumber: "917014581250",
    whatsappLink: "https://wa.me/917014581250?text=Hi%2C%20I%20would%20like%20to%20connect%20with%20you%20regarding%20wool%20sourcing%20and%20supply",
    googleMapsEmbed: "https://maps.google.com/maps?q=Bulaki+Chand+Kesari+Chand,+B-26,+Un+mandi,+Antyodaya+Nagar,+Bikaner,+Rajasthan+334001&output=embed",
    googleMapsUrl: "https://www.google.com/maps/dir//Bulaki+Chand+Kesari+Chand,+B-26,+Un+mandi,+Antyodaya+Nagar,+Bikaner,+Rajasthan+334001/@28.0229343,73.3119156,15z/data=!4m8!4m7!1m0!1m5!1m1!1s0x393fdd6f5da849cf:0xe67e12bfb7858686!2m2!1d73.2807272!2d28.0438528?entry=ttu&g_ep=EgoyMDI2MDgxMi4wIKXMDSoASAFQAw%3D%3D",
    googleMapsPlaceholderText: "Visit S.K. Woollens",
  },

  socialLinks: {
    // Configurable placeholders - do not hardcode fake URLs
    instagram_url: "",
    facebook_url: "",
    linkedin_url: "",
    whatsapp_url: "https://wa.me/917014581250?text=Hi%2C%20I%20would%20like%20to%20connect%20with%20you%20regarding%20wool%20sourcing%20and%20supply",
  },

  specifications: {
    targetMicronHighlight: "30+ Micron & 32–34+ Micron",
    targetApplication: "Carpet Yarn & Heavy Industrial Yarn Manufacturing",
    parameters: [
      {
        id: "micron",
        title: "Micron (Fibre Diameter)",
        highlight: "30+ Micron & 32–34+ Micron Specialized",
        description:
          "Sourced according to technical spinning requirements. Particularly suited for robust carpet yarn, resilience, and heavy textile applications.",
      },
      {
        id: "staple_length",
        title: "Staple Length",
        highlight: "50mm – 100mm+ Variations",
        description:
          "Consistent staple length tailored to woolen and semi-worsted carding and spinning systems.",
      },
      {
        id: "yield",
        title: "Fibre Yield",
        highlight: "Optimized Clean Yield %",
        description:
          "Accurate yield assessment ensuring cost-effective processing for commercial mills.",
      },
      {
        id: "strength",
        title: "Fibre Strength & Crimp",
        highlight: "High Tensile Durability",
        description:
          "Strong natural resilience and tensile strength to withstand high-speed spinning.",
      },
      {
        id: "colour",
        title: "Colour & Whiteness",
        highlight: "White, Cream & Natural Shades",
        description:
          "Graded carefully for uniform dyeing or natural yarn production.",
      },
      {
        id: "cleanliness",
        title: "Vegetable Matter & Cleanliness",
        highlight: "Low VM / Controlled Content",
        description:
          "Strict evaluation of vegetable matter, burrs, and impurities for efficient scouring and carding.",
      },
      {
        id: "condition",
        title: "Wool Condition",
        highlight: "Greasy & Scoured Available",
        description:
          "Supplying raw greasy wool in original bales as well as pre-scoured wool lots.",
      },
      {
        id: "origin",
        title: "Origin & Grading",
        highlight: "Domestic & Expanding International Origins",
        description:
          "Rigorous lot inspection and origin-verified lots matching buyer manufacturing sheets.",
      },
    ],
  },

  internationalMarkets: [
    {
      country: "Australia",
      code: "AU",
      title: "Australian Wool Sourcing",
      description: "Developing sourcing and exporter relationships across reputable Australian wool merchants.",
      types: "Greasy & Scoured Bales, Commercial Lots",
      status: "Developing International Relationship",
    },
    {
      country: "New Zealand",
      code: "NZ",
      title: "New Zealand Wool Sourcing",
      description: "Establishing direct links with New Zealand wool merchants for clean, high-yield coarse and medium fibre grades.",
      types: "Crossbred Fleece, Carpet Grades, Scoured Lots",
      status: "Developing International Relationship",
    },
    {
      country: "China",
      code: "CN",
      title: "Chinese Wool Sourcing",
      description: "Building B2B supplier partnerships with premier Chinese raw wool handlers and trading houses.",
      types: "Raw Fleece, Coarse Grades, Processed Lots",
      status: "Developing International Relationship",
    },
    {
      country: "Syria",
      code: "SY",
      title: "Syrian Wool Sourcing",
      description: "Cultivating regional supplier relationships for high-resilience Middle Eastern wool varieties valued in carpet weaving.",
      types: "Carpet Grade Raw Wool, Natural Fleece",
      status: "Developing Regional Relationship",
    },
    {
      country: "Other International Markets",
      code: "GLOBAL",
      title: "Global Origins Sourcing",
      description: "Evaluating additional global wool producing regions aligned with specific client micron and yield specifications.",
      types: "Custom Buyer Specifications",
      status: "Open to Supply Partnerships",
    },
  ],

  buyerTypes: [
    "Yarn Manufacturer",
    "Carpet Yarn Manufacturer",
    "Wool Processor",
    "Textile Manufacturer",
    "Trader",
    "Exporter",
    "Other",
  ],
};
